// Remove the unused import
// Import anything else you need here

export interface UserJoin {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

export interface UserConnect {
  email: string;
  password: string;
}

interface communitySnapshot {
  groupId: string; // Keep as groupId for now to maintain compatibility with existing data structure
  groupName: string; // Keep as groupName for now
  groupDescription: string; // Keep as groupDescription for now
}

export interface Metrics {
  logins: number;
  views: number;
  likesReceived: number;
  likesGiven: number;
  communitiesJoined: number;
  posts: number;
  shares: number;
  directMessages: number;
}

export interface GroupProfileUserDetails {
  userId: string; // So we know the Firestore user ID
  firstName: string;
  lastName: string;
  connected: string;
}

// Define visibility options for user profiles
export enum ProfileVisibility {
  Private = "private", // Only the user can see their profile
  Affiliations = "affiliations", // Only users sharing affiliations can see their profile
  Public = "public", // All logged-in users can see their profile
}

// Define affiliations visibility settings
export interface AffiliationVisibilitySetting {
  affiliationId: string;
  visibilityEnabled: boolean; // If true, this affiliation is used for visibility checks
}

// Define a type for a user's affiliation
export interface UserAffiliationEntry {
  id: string; // Affiliation ID
  name: string; // Affiliation name
  isPublic: boolean; // Whether this affiliation is displayed publicly
  visibilityEnabled?: boolean; // Whether this affiliation is used for visibility checks (when profileVisibility is 'affiliations')
}

export interface CustomUserFields {
  userId: string;
  email: string;
  firstName: string; // User's first name
  lastName: string; // User's last name
  location: string; // Location of the user (city, country, etc.)
  handle: string; // Unique username or handle
  affiliations: UserAffiliationEntry[] | null;
  profilePicture: string | null; // URL to the profile picture
  bio: string | null; // User's bio or about section
  dateOfBirth: Date | null; // User's date of birth
  joinedDate: Date;

  // Custom Editable Fields
  jobTitle: string; // User's job title
  quote: string; // User's personal quote

  // Academic Information
  graduationYear?: string; // Class of year (e.g., "2024", "2025")
  house?: string; // House or dormitory name
  dormitory?: string; // Alternative field for dormitory (for compatibility)

  // Club interests — MemberClub tenants only (shown as the profile's second column in
  // place of the alumni Lineage section; editable in Settings). Up to 4 entries; the
  // member chooses their own headings ("Signature Drink…" etc. are placeholder examples).
  clubInterests?: Array<{ label: string; value: string }>;

  // Social/Activity Metrics
  followersCount: number; // Number of followers
  followingCount: number; // Number of users the user follows
  postsCount: number; // Number of posts made by the user
  likesGiven: number; // Number of posts liked by the user
  commentsCount: number; // Number of comments made by the user
  communitiesJoined: communitySnapshot[]; // List of community IDs the user is a member of

  // Staff Status
  isStaff: boolean; // Whether the user is a staff member with website-wide privileges
  isCommunityAdmin: string[]; // Array of community UIDs that this user is an admin of
  institutionalAdmin: string[]; // Array of affiliation IDs that this user is an institutional admin of
  isTrustee: string[]; // Array of affiliation IDs that this user is a trustee of (affiliation-scoped moderation)

  // Preferences
  language: string; // Preferred language of the user
  theme: "light" | "dark"; // User's UI theme preference
  notificationsEnabled: boolean; // Whether the user has enabled notifications
  interests: string[]; // Array of topics/interests the user follows

  // Privacy Settings
  isProfilePrivate: boolean; // Whether the user's profile is private (legacy field - kept for backward compatibility)
  profileVisibility: ProfileVisibility; // Enhanced profile visibility setting
  directoryVisibility: ProfileVisibility; // Controls who can see the user in directory searches
  affiliationsVisibilitySettings: AffiliationVisibilitySetting[]; // Controls which affiliations are used for visibility checks
  blockedUsers: string[]; // List of user IDs blocked by the user

  // Account Management
  accountStatus: "active" | "deactivated" | "banned" | "paused"; // Current account status
  pausedUntil: Date | null; // Date until which the user is paused (if accountStatus is paused)
  lastLogin: Date; // Last login date
  verified: boolean; // Whether the user account is verified
  passwordResetRequired: boolean; // If the user needs to reset their password
  hasCompletedOnboarding: boolean; // Whether the user has completed the onboarding process
  agreedToTermsAt: Date | null; // When the user agreed to terms of service (legacy - kept for backward compatibility)
  agreedToGuidelinesAt: Date | null; // When the user agreed to community guidelines (legacy - kept for backward compatibility)
  agreedToPrivacyAt: Date | null; // When the user agreed to privacy policy (legacy - kept for backward compatibility)
  mainAppTOSAcknowledgedAt: Date | null; // When the user acknowledged TOS in main app
  mainAppPrivacyAcknowledgedAt: Date | null; // When the user acknowledged privacy policy in main app
  mainAppGuidelinesAcknowledgedAt: Date | null; // When the user acknowledged community guidelines in main app
  whitelabelTOSAcknowledgedAt: Date | null; // When the user acknowledged TOS in whitelabel app
  whitelabelPrivacyAcknowledgedAt: Date | null; // When the user acknowledged privacy policy in whitelabel app
  whitelabelGuidelinesAcknowledgedAt: Date | null; // When the user acknowledged community guidelines in whitelabel app
  lastLegalDocumentVersion: {
    termsOfService: number;
    privacyPolicy: number;
    communityGuidelines: number;
  } | null; // Track last acknowledged version of each legal document
  emailVerified: boolean; // Whether the user has verified their email address

  // Contact Information
  phoneNumber: string | null; // User's phone number
  secondaryEmail: string | null; // User's secondary email for recovery

  // Activity Tracking
  lastPostDate: Date | null; // Date of the user's last post
  recentSearches: string[]; // Array of recent search terms used by the user
  devices: string[]; // List of devices logged into the user's account

  // Analytics (optional)
  loginMetrics: number; // Percentage of engagement rate based on activity
  viewsMetrics: number; // Percentage of engagement rate based on activity
  likesMetrics: number; // Percentage of engagement rate based on activity
  commentsMetrics: number; // Percentage of engagement rate based on activity

  // New fields for subscription
  subscription: SubscriptionTier;
  subscriptionStartDate: Date;
  subscriptionEndDate: Date | null;

  // Add messaging permissions to the CustomUserFields interface
  messagingPermissions?: {
    [affiliationId: string]: boolean; // true if messaging is enabled for this affiliation
  };
  communityMessagingPermissions?: {
    [communityId: string]: boolean; // true if messaging is enabled for this community
  };

  // Additional fields
  communities?: string[]; // Array of community IDs the user belongs to

  // Network Pulse
  lastPulseViewedAt?: Date | null; // When the user last opened their Network Pulse
}

export enum SubscriptionTier {
  Basic = "Basic",
  Premium = "Premium",
  Enterprise = "Enterprise",
  Signature = "Signature",
}

// Extend Firebase User
export type AppUser = {
  uid: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string | null;
  bio?: string | null;
  email?: string;
  handle?: string;
  isVerified?: boolean;
  subscription?: SubscriptionTier;
  subscriptionStartDate?: Date;
  subscriptionEndDate?: Date | null;
} & Partial<CustomUserFields>;
